/**
 * tva-reduite-content.ts
 * ─────────────────────────────────────────────────────────────────
 * Source unique du contenu éditorial de la page-guide
 * /particuliers/aides/tva-reduite.
 *
 * RÈGLES ÉDITORIALES (AGENTS §8) :
 *  - Pas de promesse "automatique" sans rappeler l'attestation à signer.
 *  - Préciser : TVA 5,5 % nécessite l'attestation à signer.
 *  - Préciser : pas de critère de certification pour la TVA 5,5 % seule.
 */

// ════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════

export interface TvaSummaryPoint {
  text: string;
}

export interface TvaTaux {
  taux: string;
  label: string;
  description: string;
  color: 'success' | 'warning' | 'error';
  examples: string[];
}

export interface TvaTravauxCategory {
  iconName: string;
  title: string;
  items: string[];
}

export interface TvaCondition {
  iconName: string;
  title: string;
  description: string;
}

export interface TvaCumul {
  aide: string;
  iconName: string;
  description: string;
  tone: 'brand' | 'accent' | 'sage' | 'lavender' | 'ivory';
  href?: string;
}

export interface TvaFaqItem {
  question: string;
  answer: string;
}

// ════════════════════════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════════════════════════

export const TVA_HERO = {
  eyebrow: 'TVA à taux réduit',
  titlePrimary: 'TVA à 5,5 %',
  titleAccent: "l'économie automatique sur vos travaux",
  lead:
    "La TVA à taux réduit de 5,5 % s'applique sur les travaux d'amélioration énergétique des logements de plus de 2 ans. Contrairement aux autres aides, c'est une économie immédiate : l'artisan facture directement au bon taux, sans démarche complexe. Économie de 14,5 points sur la facture par rapport au taux plein de 20 %.",
  amountHeadline: '14,5 points',
  amountSubline:
    'Économie immédiate sur la facture : TVA 5,5 % au lieu de 20 %. Sans condition de ressources, sans plafond.',
  microPromise: 'Appliquée par votre artisan. Une attestation à signer. Pour les logements de plus de 2 ans.',
} as const;

export const TVA_SUMMARY: TvaSummaryPoint[] = [
  {
    text:
      "La TVA à 5,5 % est appliquée directement par l'artisan sur sa facture, sans démarche fiscale complexe de votre part.",
  },
  {
    text:
      "Conditions principales : logement achevé depuis plus de 2 ans, travaux d'amélioration énergétique éligibles, attestation signée par le client.",
  },
  {
    text:
      "S'applique à la fois sur la main-d'œuvre et sur le matériel facturé par l'artisan (pas sur les achats directs en magasin).",
  },
  {
    text:
      "Pas besoin de certification particulière pour la TVA 5,5 % seule. La certification devient nécessaire pour cumuler avec les autres aides.",
  },
];

// ════════════════════════════════════════════════════════════════
// MÉCANISME
// ════════════════════════════════════════════════════════════════

export const TVA_MECANISME = {
  eyebrow: 'Comment ça marche',
  title: "Une économie immédiate, applicable par l'artisan",
  description:
    "La TVA à 5,5 % est encadrée par le Code général des impôts. Trois taux de TVA coexistent pour les travaux sur logements anciens : 5,5 % (travaux d'amélioration énergétique), 10 % (autres travaux d'entretien / amélioration), 20 % (travaux neufs / non éligibles). C'est à l'artisan d'appliquer le bon taux selon la nature de l'intervention.",
} as const;

// ════════════════════════════════════════════════════════════════
// COMPARATIF TVA 5,5 / 10 / 20
// ════════════════════════════════════════════════════════════════

export const TVA_TAUX: TvaTaux[] = [
  {
    taux: '5,5 %',
    label: 'Taux réduit (énergétique)',
    color: 'success',
    description:
      "Travaux d'amélioration de la qualité énergétique du logement, ainsi que les travaux induits qui leur sont indissociables.",
    examples: [
      'Isolation thermique',
      'Pompe à chaleur, chaudière biomasse',
      'Chauffe-eau solaire / thermodynamique',
      'VMC double flux',
      'Travaux induits indissociables',
    ],
  },
  {
    taux: '10 %',
    label: 'Taux intermédiaire',
    color: 'warning',
    description:
      "Autres travaux d'amélioration, de transformation, d'aménagement et d'entretien dans les logements achevés depuis plus de 2 ans.",
    examples: [
      'Plomberie, électricité (hors énergétique)',
      'Peinture, papier peint',
      'Revêtements de sols',
      'Aménagements intérieurs',
    ],
  },
  {
    taux: '20 %',
    label: 'Taux normal',
    color: 'error',
    description:
      'Travaux de construction neuve, d\'extension, ou réalisés sur des logements de moins de 2 ans. Taux plein appliqué.',
    examples: [
      'Construction neuve',
      'Surélévation, extension',
      'Logements < 2 ans',
      'Aménagement de combles (gain de surface)',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// TRAVAUX ÉLIGIBLES
// ════════════════════════════════════════════════════════════════

export const TVA_TRAVAUX: TvaTravauxCategory[] = [
  {
    iconName: 'snowflake',
    title: 'Isolation thermique',
    items: [
      'Isolation des combles, des planchers bas, des murs',
      'Remplacement de fenêtres / portes-fenêtres',
      'Pose de protections solaires',
      'Travaux induits indissociables (raccordements, finitions)',
    ],
  },
  {
    iconName: 'flame',
    title: 'Chauffage & ECS performants',
    items: [
      'Pompe à chaleur (air-eau, géothermique, hybride)',
      'Chaudière biomasse, poêle à granulés',
      'Chauffe-eau solaire / thermodynamique',
      'Système solaire combiné',
      'Raccordement à un réseau de chaleur renouvelable',
    ],
  },
  {
    iconName: 'wind',
    title: 'Ventilation & régulation',
    items: [
      'VMC double flux',
      'Ventilation hygroréglable performante',
      'Programmateurs et régulateurs de chauffage',
      'Compteurs individuels de chauffage / eau chaude',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// CONDITIONS
// ════════════════════════════════════════════════════════════════

export const TVA_CONDITIONS: TvaCondition[] = [
  {
    iconName: 'home',
    title: 'Logement de plus de 2 ans',
    description:
      'Logement (résidence principale ou secondaire) achevé depuis plus de 2 ans à la date de début des travaux.',
  },
  {
    iconName: 'users',
    title: 'Propriétaire, locataire ou occupant',
    description:
      'Vous pouvez bénéficier de la TVA réduite quel que soit votre statut : propriétaire occupant, propriétaire bailleur, locataire, syndicat de copropriétaires.',
  },
  {
    iconName: 'wrench',
    title: "Travaux d'amélioration énergétique",
    description:
      'Travaux éligibles définis par la loi : isolation, chauffage performant, énergies renouvelables, ventilation, régulation.',
  },
  {
    iconName: 'file-signature',
    title: 'Attestation à signer',
    description:
      "Attestation simplifiée (travaux de moins de 300 €) ou normale, signée par le client et remise à l'artisan avant la facturation. Conservez une copie pendant 5 ans.",
  },
];

// ════════════════════════════════════════════════════════════════
// POINTS DE VIGILANCE
// ════════════════════════════════════════════════════════════════

export const TVA_PIEGES: Array<{ iconName: string; title: string; description: string }> = [
  {
    iconName: 'shield-check',
    title: "Ne pas oublier l'attestation",
    description:
      "Sans attestation signée par le client, l'artisan ne peut pas appliquer la TVA 5,5 %. Le formulaire officiel est téléchargeable sur impots.gouv.fr.",
  },
  {
    iconName: 'snowflake',
    title: 'Matériel acheté directement en magasin',
    description:
      "Si vous achetez le matériel vous-même en magasin et que l'artisan ne fait que la pose, c'est la TVA 20 % qui s'applique sur votre achat. Pour bénéficier de la TVA 5,5 %, l'artisan doit fournir et poser le matériel.",
  },
  {
    iconName: 'hard-hat',
    title: 'Travaux induits non éligibles',
    description:
      "Seuls les travaux induits indissociables aux travaux énergétiques bénéficient du 5,5 %. Les autres travaux d'entretien (peinture, sols) restent à 10 %, et les travaux neufs / d'extension à 20 %.",
  },
];

// ════════════════════════════════════════════════════════════════
// CUMUL
// ════════════════════════════════════════════════════════════════

export const TVA_CUMULS: TvaCumul[] = [
  {
    aide: "MaPrimeRénov'",
    iconName: 'piggy-bank',
    description:
      "Cumul automatique : la TVA 5,5 % s'applique sur la facture, et l'aide de l'État se calcule sur le montant des travaux.",
    tone: 'brand',
    href: '/particuliers/aides/maprimerenov',
  },
  {
    aide: 'Prime énergie',
    iconName: 'sparkles',
    description:
      "Cumulable sans restriction. Les deux dispositifs sont indépendants : la TVA réduit le coût, la prime énergie s'ajoute en déduction.",
    tone: 'accent',
    href: '/particuliers/aides/cee',
  },
  {
    aide: 'Éco-PTZ',
    iconName: 'banknote',
    description:
      "Le prêt à taux zéro finance le reste à charge, déjà à 5,5 %. Résultat : vous rénovez sans toucher à votre trésorerie.",
    tone: 'sage',
    href: '/particuliers/aides/eco-ptz',
  },
  {
    aide: 'Coup de pouce',
    iconName: 'flame',
    description:
      "La TVA 5,5 % se cumule avec les primes majorées, sans interférence.",
    tone: 'lavender',
    href: '/particuliers/aides/coup-de-pouce',
  },
];

// ════════════════════════════════════════════════════════════════
// FAQ
// ════════════════════════════════════════════════════════════════

export const TVA_FAQ: TvaFaqItem[] = [
  {
    question: 'Quels travaux bénéficient de la TVA à 5,5 % ?',
    answer:
      "Les travaux d'amélioration énergétique : isolation thermique (combles, murs, planchers, fenêtres), équipements de chauffage performants (pompe à chaleur, chaudière biomasse, poêle granulés), équipements utilisant des énergies renouvelables, régulation du chauffage, VMC double flux. Les travaux induits indissociables (raccordements électriques, finitions) bénéficient aussi du taux réduit.",
  },
  {
    question: 'Faut-il un professionnel certifié ?',
    answer:
      "Non. Contrairement aux autres aides, la TVA à 5,5 % ne nécessite pas de certification. Toutefois, si vous souhaitez cumuler la TVA réduite avec l'aide de l'État ou la prime énergie, l'artisan devra être certifié pour ces aides-là.",
  },
  {
    question: "Qu'est-ce que l'attestation simplifiée ?",
    answer:
      "C'est un formulaire officiel (téléchargeable sur impots.gouv.fr) signé par le client et remis à l'artisan avant la facturation. Deux versions existent : attestation simplifiée pour les travaux de moins de 300 €, et attestation normale au-delà. Conservez une copie pendant 5 ans.",
  },
  {
    question: "Que faire si l'artisan facture à 20 % au lieu de 5,5 % ?",
    answer:
      "Si les conditions d'éligibilité sont réunies (logement > 2 ans, travaux énergétiques, attestation signée), demandez une facture rectificative à l'artisan. En cas de litige, vous pouvez saisir la médiation de la consommation.",
  },
  {
    question: "La TVA 5,5 % s'applique-t-elle sur le matériel ?",
    answer:
      "Oui, à condition que ce soit l'artisan qui fournisse et pose le matériel. Si vous achetez le matériel vous-même en magasin, la TVA à 20 % s'applique sur votre achat (la pose peut être facturée à 5,5 % par l'artisan). Il est presque toujours plus avantageux que l'artisan fournisse l'ensemble.",
  },
  {
    question: "La TVA 5,5 % s'applique-t-elle aux résidences secondaires ?",
    answer:
      "Oui. Contrairement à l'aide de l'État (résidence principale uniquement), la TVA à 5,5 % s'applique aux résidences principales et secondaires, dès lors que le logement a plus de 2 ans et que les travaux entrent dans la liste éligible.",
  },
  {
    question: "Quelle économie réelle représente la TVA 5,5 % ?",
    answer:
      "L'écart entre 5,5 % et 20 % est de 14,5 points. Sur une facture de 10 000 €, vous payez 10 550 € au lieu de 12 000 €, soit 1 450 € d'économie immédiate. Sur une rénovation globale à 40 000 €, l'économie atteint 5 800 € — soit l'équivalent d'une aide majeure.",
  },
];
